import React, { useEffect, useRef, useState } from 'react';
import { Call, Category, getMyCalls, getCategories, getCall } from '../api';
import { CallEditForm } from '../components/CallEditForm';
import { PageLayout } from '../../../components/common/PageLayout';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { useIsCommunicationAdmin } from '../hooks/useIsCommunicationAdmin';
import { useIsCommunicationSupervisor } from '../hooks/useIsCommunicationSupervisor';
import { formatDateTimeLocal, formatPhoneNumber } from '../format';
import { buildCallsConnection } from '../realtime';
import { ui } from '../ui';

export const MyCallsPage: React.FC = () => {
  const isAdmin = useIsCommunicationAdmin();
  const isSupervisor = useIsCommunicationSupervisor();
  const navItems = [{ path: '/communication', label: 'My Calls' }];
  if (isAdmin || isSupervisor) navItems.push({ path: '/communication/admin', label: 'Admin' });

  const [calls, setCalls] = useState<Call[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('NeedsReview');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [editCall, setEditCall] = useState<Call | null>(null);
  const [draft, setDraft] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Track which call ids we've already shown so a poll/refresh can flag genuinely new arrivals.
  // null = not yet baselined (first load of the current filter), so we don't flash the whole list.
  const seenIdsRef = useRef<Set<number> | null>(null);
  const [newIds, setNewIds] = useState<Set<number>>(new Set());

  const load = async () => {
    const next = await getMyCalls(statusFilter || undefined);
    setCalls(next);
    const ids = new Set(next.map(c => c.CallID));
    if (seenIdsRef.current === null) {
      seenIdsRef.current = ids;   // baseline the current view without highlighting
      return;
    }
    const fresh = [...ids].filter(id => id !== 0 && !seenIdsRef.current!.has(id));
    seenIdsRef.current = ids;
    if (fresh.length === 0) return;
    setNewIds(prev => new Set([...prev, ...fresh]));
    // Drop the highlight once the ~1s fade finishes, so the row can flash again if it recurs.
    setTimeout(() => {
      setNewIds(prev => { const n = new Set(prev); fresh.forEach(id => n.delete(id)); return n; });
    }, 1200);
  };

  useEffect(() => { getCategories().then(setCategories); }, []);
  // Re-baseline on filter change so switching views doesn't flash every row as "new".
  useEffect(() => { seenIdsRef.current = null; setNewIds(new Set()); load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  // Pause the live connection while an edit/draft form is open so a push-triggered refetch
  // can't yank the list out from under an in-progress edit.
  const formOpen = expanded !== null || draft;

  // Real-time push via SignalR (WebSocket): the server broadcasts CallIngested to this agent's
  // group the instant a call lands, so the list updates live — no polling. A ref keeps the
  // handler pointed at the latest load() (which closes over the current status filter).
  // onreconnected refetches after a dropped socket so we catch anything missed while offline.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (formOpen) return;
    const conn = buildCallsConnection();
    conn.on('CallIngested', () => { loadRef.current(); });
    conn.onreconnected(() => { loadRef.current(); });
    conn.start().catch(err => console.error('SignalR connect failed', err));
    return () => { conn.stop().catch(() => { /* already closing */ }); };
  }, [formOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // A completed call locks 12h after the call time (mirrors the server's edit window); older
  // completed records are read-only. Kept in sync with EditWindow in CommunicationController.
  const EDIT_WINDOW_MS = 12 * 60 * 60 * 1000;
  const isLocked = (c: Call) =>
    c.Status === 'Completed' && Date.now() - new Date(c.CallTimeUtc).getTime() > EDIT_WINDOW_MS;

  // Toggle the inline edit form. The list endpoints don't hydrate Categories, so fetch the
  // full call before editing — otherwise Save would send an empty category set and wipe them.
  const toggleEdit = async (c: Call) => {
    if (expanded === c.CallID) { setExpanded(null); setEditCall(null); return; }
    setDraft(false);
    setEditCall(await getCall(c.CallID));
    setExpanded(c.CallID);
  };

  const closeEdit = () => { setExpanded(null); setEditCall(null); };

  // Open an unsaved draft form; nothing is written until the user clicks Save.
  const startDraft = () => { setExpanded(null); setEditCall(null); setDraft(true); };

  // Empty shell backing the draft form (CallID 0 = not yet persisted).
  const emptyDraft: Call = {
    CallID: 0, CallTimeUtc: '', IsMissed: false,
    Status: 'NeedsReview', Source: 'Manual', Categories: [],
  };

  return (
    <PageLayout navItems={navItems}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className={ui.pageTitle}>My Calls</h1>
        <div className="flex items-center gap-3">
          <select className={ui.control} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="NeedsReview">Needs Review</option>
            <option value="Completed">Completed</option>
            <option value="">All</option>
          </select>
          <button className={ui.btnSecondary} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className={ui.btnPrimary} onClick={startDraft} disabled={draft}>Add Call</button>
        </div>
      </div>

      <div className={ui.tableWrap}>
        <table className={ui.table}>
          <thead className={ui.thead}>
            <tr>
              <th className={ui.th}>Time</th>
              <th className={ui.th}>Caller</th>
              <th className={ui.th}>Account</th>
              <th className={ui.th}>Status</th>
              <th className={`${ui.th} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody className={ui.tbody}>
            {draft && (
              <tr className="bg-surface">
                <td colSpan={5} className="px-4 py-4">
                  <CallEditForm call={emptyDraft} categories={categories} isNew
                    onSaved={() => { setDraft(false); load(); }}
                    onCancel={() => setDraft(false)} />
                </td>
              </tr>
            )}
            {calls.map(c => (
              <React.Fragment key={c.CallID}>
                <tr className={`hover:bg-surface-hover${newIds.has(c.CallID) ? ' call-arrived' : ''}`}>
                  <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeLocal(c.CallTimeUtc)}</td>
                  <td className={ui.td}>{c.CallerNumber ? formatPhoneNumber(c.CallerNumber) : <span className="text-muted">—</span>}</td>
                  <td className={ui.td}>{c.AccountNumber || <span className="text-muted">—</span>}</td>
                  <td className={ui.td}><StatusBadge status={c.Status} /></td>
                  <td className={`${ui.td} text-right`}>
                    {isLocked(c) ? (
                      <span className="text-xs text-muted" title="Completed over 12 hours ago — locked">Locked</span>
                    ) : (
                      <button className={ui.btnSecondary} onClick={() => toggleEdit(c)}>
                        {expanded === c.CallID ? 'Close' : 'Edit'}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === c.CallID && editCall?.CallID === c.CallID && (
                  <tr className="bg-surface">
                    <td colSpan={5} className="px-4 py-4">
                      <CallEditForm call={editCall} categories={categories}
                        onSaved={() => { closeEdit(); load(); }} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {calls.length === 0 && !draft && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">
                  No calls to review.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
};
